import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { createApi } from '@reduxjs/toolkit/query/react';
import { formWithdrawalMethodInfo, formWithdrawalRequiredFields } from 'store/formatters';
import { createBaseQueryWithPwPrefix } from 'store/middleware/pwContextMiddleware';
import type {
  PayHubCashierContractsPaymentsSubmitPaymentGroupRequest,
  PayHubCashierContractsPayoutsInitPayoutGroupsResponse as InitSuccess,
  PayHubWidgetContractsPayoutsSelectPayoutGroupResponse as WithdrawalMethodInfoResponse,
  PayHubWidgetContractsSubmitPayoutResponse,
} from 'store/models';
import { WIDGET_API } from 'utils/constants';

export interface CustomServerError {
  status: Pick<FetchBaseQueryError, 'status'> | string | number;
  errorMessage?: string;
  errorCode?: number;
}

type ExtendedFetchBaseQueryError = FetchBaseQueryError & {
  data?: {
    error_message: string;
    error_code: number;
  };
};

export interface WithdrawalSubmitRequestProps {
  payGroupId: number;
  requestBody: PayHubCashierContractsPaymentsSubmitPaymentGroupRequest;
}

export type GetWithdrawalMethodInfoRequestProps = {
  payGroupId: number;
  currency: string;
};

export type GetWithdrawalMethodsRequestProps =
  | {
      payGroupId?: number;
    }
  | undefined;

const INIT_TAG = 'init_withdrawal';
export const SELECT_TAG = 'select_withdrawal';

export const withdrawalAPI = createApi({
  reducerPath: 'withdrawalAPI',
  tagTypes: [INIT_TAG, SELECT_TAG],
  baseQuery: createBaseQueryWithPwPrefix(WIDGET_API),
  endpoints(builder) {
    return {
      getWithdrawalMethods: builder.query<InitSuccess, string | void>({
        query: (currencyName) => ({
          url: currencyName ? `/payouts/groups/init?currency=${currencyName}` : '/payouts/groups/init',
        }),
        keepUnusedDataFor: Infinity,
        providesTags: [INIT_TAG],
      }),
    };
  },
});

export const extendedWithdrawalAPI = withdrawalAPI.injectEndpoints({
  endpoints: (builder) => ({
    getWithdrawalMethodInfo: builder.query<WithdrawalMethodInfoResponse, GetWithdrawalMethodInfoRequestProps>({
      query: ({ payGroupId, currency }) => ({
        url: `/payouts/groups/${payGroupId}/select`,
        method: 'POST',
        body: {
          currency,
        },
        keepalive: true,
      }),
      keepUnusedDataFor: Infinity,
      providesTags: (_result, _error, { payGroupId }) => [
        {
          type: SELECT_TAG,
          id: payGroupId,
        },
      ],
      transformResponse: (response: WithdrawalMethodInfoResponse, _meta, { payGroupId }) =>
        formWithdrawalMethodInfo(response, formWithdrawalRequiredFields, payGroupId) as WithdrawalMethodInfoResponse,
    }),
    submitWithdrawal: builder.query<PayHubWidgetContractsSubmitPayoutResponse, WithdrawalSubmitRequestProps>({
      query: ({ payGroupId, requestBody }) => ({
        url: `payouts/groups/${payGroupId}/submit`,
        method: 'POST',
        body: requestBody,
        keepalive: true,
      }),
      transformErrorResponse: (response: ExtendedFetchBaseQueryError): CustomServerError => ({
        status: response?.status,
        errorMessage: response?.data?.error_message,
        errorCode: response?.data?.error_code,
      }),
    }),
  }),
  overrideExisting: false,
});

export const { useGetWithdrawalMethodsQuery, useLazyGetWithdrawalMethodsQuery } = withdrawalAPI;
export const { useGetWithdrawalMethodInfoQuery, useLazyGetWithdrawalMethodInfoQuery, useLazySubmitWithdrawalQuery } =
  extendedWithdrawalAPI;

export const { endpoints } = withdrawalAPI;

export default withdrawalAPI.reducer;
